import { AppFlow } from '@lib/web-shared';

export interface UtagParam {
  channel: string;
  subchannel1: string;
  subchannel2: string;
  flow?: string;
  page?: string;
  club?: string;
  autopay?: string;
  category?: string[];
  confirmation_id?: string;
  error?: string;
  product?: string[];
  quantity?: string[];
  transaction_amount?: string[];
  transaction_type?: string;
  paymentplan?: string;
  ins_min_due?: string;
  pay_add_amount?: string;
  ins_autopaycampaign?: string;
  state?: string;
  warning?: string;
  ins_policy?: string;
  botdet?: 'security check' | 'detected' | 'success' | 'failure';
}

export class UtagHelper {
  private static botDetectionStatus: 'success' | null = null;
  private static pendingLinks: Record<string, any>[] = [];

  static setBotDetectionStatus(status: 'success' | null) {
    this.botDetectionStatus = status;
  }

  static getBotDetectionStatus(): 'success' | null {
    return this.botDetectionStatus;
  }

  static clearBotDetectionStatus() {
    this.botDetectionStatus = null;
  }

  static clearPendingLinks() {
    this.pendingLinks = [];
  }

  static getPendingLinks(): Record<string, any>[] {
    return [...this.pendingLinks];
  }

  private static flushPendingLinks() {
    if (typeof (window as any).utag !== 'undefined') {
      while (this.pendingLinks.length > 0) {
        const linkTags = this.pendingLinks.shift();
        if (linkTags) {
          (window as any).utag.link(linkTags);
        }
      }
    }
  }

  static loadCommonTags = (appFlow = AppFlow.PAYMENT): UtagParam => {
    return {
      channel: 'my account',
      subchannel1: 'insurance',
      subchannel2: appFlow === AppFlow.AUTOPAY ? 'easy auto pay' : 'easy pay',
      flow: appFlow === AppFlow.AUTOPAY ? 'stand alone auto pay' : 'obp:easy pay',
    };
  };

  static Errors = {
    systemError: 'system error',
    paymentError: 'payment error',
    systemErrorAutoPayFlow: 'error-system',
    autoPayErrorAutoPayFlow: 'error-auto-pay',
    paymentErrorAutoPayFlow: 'error-payment',
    notFoundError: 'page is no longer available',
  };

  static getPages = (appFlow = AppFlow.PAYMENT) => {
    const pageName = appFlow === AppFlow.AUTOPAY ? 'easy auto pay' : 'easy pay';
    return {
      submitInsurancePage: `my account:insurance:${pageName}:enter your policy number`,
      verifyPolicyInfoPage: `my account:insurance:${pageName}:confirm last name`,
      paymentAmountSelectionPage: `my account:insurance:${pageName}:review payment`,
      noEmailOnFilePage: `my account:insurance:${pageName}:no email on file`,
      emailOnFilePage: `my account:insurance:${pageName}:email on file`,
      confirmationPage: `my account:insurance:${pageName}:confirmation`,
      expiredConfirmationPage: `my account:insurance:${pageName}:confirmation-reload`,
      autoPayPage: `my account:insurance:${pageName}:${appFlow === AppFlow.AUTOPAY ? 'enrollment' : 'autopay'}`,
      alreadyPaid: `my account:insurance:${pageName}:already paid`,
      systemErrorPage: `my account:insurance:${pageName}:${
        appFlow === AppFlow.AUTOPAY ? UtagHelper.Errors.systemErrorAutoPayFlow : UtagHelper.Errors.systemError
      }`,
      paymentErrorPage: `my account:insurance:${pageName}:${
        appFlow === AppFlow.AUTOPAY ? UtagHelper.Errors.paymentErrorAutoPayFlow : UtagHelper.Errors.paymentError
      }`,
      notFoundPage: `my account:insurance:${pageName}:error-generic`,
    };
  };

  static loadUtagJs() {
    if ((window as any).utagLoadAttempt) {
      if (typeof (window as any).utag !== 'undefined') {
        this.flushPendingLinks();
      }
      return;
    }
    (window as any).utagLoadAttempt = true;
    const utagJsScript = document.createElement('script');
    utagJsScript.src = '//tms.ace.aaa.com/ace/prod/utag.js';
    utagJsScript.type = 'text/javascript';
    utagJsScript.async = true;
    utagJsScript.addEventListener('load', () => {
      this.flushPendingLinks();
    });
    document.body.appendChild(utagJsScript);
  }

  static link(tags: Partial<UtagParam> | Record<string, any>) {
    const currentValues = (window as any).aceMediaTagValues || {};
    const linkTags = { ...currentValues, ...tags };
    this.dispatchTntSpaEvent(linkTags);
    if (typeof (window as any).utag === 'undefined') {
      this.pendingLinks.push(linkTags);
      this.loadUtagJs();
    } else {
      (window as any).utag.link(linkTags);
    }
  }

  static fireBotTag(botdet: 'security check' | 'detected' | 'failure', extraFields?: Record<string, string>) {
    this.link({ botdet, ...extraFields });
  }

  static tag(tags: UtagParam) {
    const finalTags: UtagParam = { ...tags };
    if (this.botDetectionStatus && !finalTags.botdet) {
      finalTags.botdet = this.botDetectionStatus;
      this.clearBotDetectionStatus();
    } else if (finalTags.botdet) {
      this.clearBotDetectionStatus();
    }

    (window as any).aceMediaTagValues = { ...finalTags };
    this.dispatchTntSpaEvent((window as any).aceMediaTagValues);
    if (typeof (window as any).utag === 'undefined') {
      this.loadUtagJs();
    } else {
      this.flushPendingLinks();
      (window as any).utag.view((window as any).aceMediaTagValues);
    }
  }

  static dispatchTntSpaEvent(tags: UtagParam | Record<string, any>) {
    document.dispatchEvent(
      new CustomEvent('tnt_spa', {
        detail: tags,
      })
    );
  }
}
